import React, { useMemo } from 'react';
import { RouteProp } from '@react-navigation/native';
import { getIntl } from '../../../../../utils/intl';
import useSetHeaderTitle from '../../../../../hooks/useSetHeaderTitle';
import { ProfilesTabParamList, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import CareTeamMemberList from '../../../../shared/CareTeamMemberList';
import NotesList from '../../../../shared/Notes/NotesList';
import useNotes, { NoteQueryArgs } from '../../../../../hooks/api/useNotes';
import addCountToString from '../../../../../utils/addCountToString';
import { TabView, TabOption } from '../../../../v2BaseComponents/TabView';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import { useHasPermission } from '../../../../v2BaseComponents/Permissions/hooks';
import ProfileHeaderContainer from '../../../../v2BaseComponents/ProfileHeaderContainer';
import { APITypes } from '../../../../../primary-record-types';
import { useAppType } from '../../../../../hooks/useAppType';
import useOrganizationCareTeamMembers from '../../../../../hooks/api/useOrganizationCareTeamMembers';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import { useIsMobileLayout } from '../../../../../hooks/useIsMobileLayout';
import useOrganization from '../../../../../hooks/api/useOrganization';
import { isPresent } from 'ts-is-present';
import { useMaybeRenderedSideDrawerToggle } from '../../../../v2BaseComponents/SideDrawer/MaybeRenderedSideDrawer';
import CopyOrganizationCareTeamMemberSideDrawerContent from './CopyOrganizationCareTeamMemberSideDrawerContent';

const intl = getIntl('CareTeam', {
    header: `Care Team`,
    careTeam: 'Care Team',
    add: 'Add a Care Team Member',
    details: 'Care Team Member Details',
    edit: 'Edit Care Team Member',
    addRelatedCareTeamMember: 'Add Related Care Team Member',
    list: 'List',
    notes: 'Notes',
    addOrgResource: 'Add {organizationName} Resource',
});

type NotApplicableProCareTeamResourcesData = {
    status: 'not-applicable';
};

type LoadingProCareTeamResourcesData = {
    status: 'loading';
};

type LoadedProCareTeamResourcesData = {
    status: 'loaded';
    careTeamMembers: APITypes.organizationService.OrganizationCareTeamMemberDetails[];
    organizationName: string;
};

type ProCareTeamResourcesData =
    | NotApplicableProCareTeamResourcesData
    | LoadingProCareTeamResourcesData
    | LoadedProCareTeamResourcesData;

type CareTeamScreenContentsProps = {
    profileId: string;
    proCareTeamResourcesData: ProCareTeamResourcesData;
};

type ProCareTeamResourcesProviderProps = {
    children: (props: CareTeamScreenContentsProps) => React.ReactNode;
    profileId: string;
};

const ProfessionalAppCareTeamResourcesProvider: React.FC<ProCareTeamResourcesProviderProps> = ({
    children,
    profileId,
}) => {
    const { organizationCareTeamMembers, isLoadingOrganizationCareTeamMembers } = useOrganizationCareTeamMembers({});
    const { myOrganization, isLoadingMyOrganization } = useOrganization({});
    if (isLoadingOrganizationCareTeamMembers || isLoadingMyOrganization) {
        return children({
            profileId,
            proCareTeamResourcesData: { status: 'loading' },
        });
    } else {
        return children({
            profileId,
            proCareTeamResourcesData: {
                status: 'loaded',
                careTeamMembers: organizationCareTeamMembers,
                organizationName: myOrganization?.organization.metadata.name || '',
            },
        });
    }
};

const ProCareTeamResourcesProvider: React.FC<ProCareTeamResourcesProviderProps> = ({ children, profileId }) => {
    const appType = useAppType();
    const isMobileLayout = useIsMobileLayout(); // only show on desktop due to side drawer.  If this is needed in the future, we can add a mobile layout
    if (appType !== 'professional' || isMobileLayout) {
        return children({
            profileId,
            proCareTeamResourcesData: { status: 'not-applicable' },
        });
    } else {
        return (
            <ProfessionalAppCareTeamResourcesProvider profileId={profileId}>
                {children}
            </ProfessionalAppCareTeamResourcesProvider>
        );
    }
};

const CareTeamScreenContents: React.FC<CareTeamScreenContentsProps> = ({ profileId, proCareTeamResourcesData }) => {
    const [tab, setTab] = React.useState<Tab>('list');
    const hasEditCareTeamPermission = useHasPermission(profileId, 'careTeam', 'edit');
    const noteQueryArgs = useMemo<NoteQueryArgs>(
        () => ({
            medicalProfileId: profileId,
            entityType: 'care-team',
        }),
        [profileId],
    );
    const { notes } = useNotes({ noteQueryArgs });
    const addProCareTeamResourcesSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'addProCareTeamMember',
        title:
            proCareTeamResourcesData.status === 'loaded'
                ? intl('addOrgResource', {
                      organizationName: proCareTeamResourcesData.organizationName,
                  })
                : '',
        renderContent: (onClose) => (
            <CopyOrganizationCareTeamMemberSideDrawerContent profileId={profileId} onClose={onClose} />
        ),
        mobileOnFocus: () => {}, // only for desktop
    });
    const tabs: TabOption[] = [
        {
            value: 'list',
            title: intl('list'),
            renderTabContent: () => (
                <ActionButtonLayout
                    actions={[
                        {
                            title: intl('add'),
                            onPress: () =>
                                navigation.navigate('add-care-team', {
                                    profileId: profileId,
                                }),
                            icon: 'PlusCircle' as const,
                            show: hasEditCareTeamPermission,
                            testID: 'add-care-team-member-button',
                            displayInMoreMenu: false,
                        },
                        proCareTeamResourcesData.status === 'loaded' &&
                        !!proCareTeamResourcesData.careTeamMembers.length
                            ? {
                                  title: intl('addOrgResource', {
                                      organizationName: proCareTeamResourcesData.organizationName,
                                  }),
                                  onPress: addProCareTeamResourcesSideDrawer.onOpen,
                                  icon: 'PlusCircle' as const,
                                  show: hasEditCareTeamPermission,
                                  testID: 'add-org-care-team-member-button',
                                  displayInMoreMenu: false,
                              }
                            : null,
                    ].filter(isPresent)}
                >
                    <CareTeamMemberList
                        enableAdd
                        profileId={profileId}
                        onSelect={(careTeamMemberId: string) => {
                            navigation.navigate('care-team-member', {
                                profileId: profileId,
                                careTeamMemberId: careTeamMemberId,
                            });
                        }}
                    />
                </ActionButtonLayout>
            ),
        },
        {
            value: 'notes',
            title: addCountToString(intl('notes'), notes?.notes?.length),
            renderTabContent: () => (
                <NotesList
                    noteQueryArgs={noteQueryArgs}
                    onAddNote={(args) => {
                        navigation.navigate('add-note', args);
                    }}
                    onViewDetails={(note) => {
                        navigation.navigate('note', {
                            noteId: note.noteId,
                            noteEntityType: note.entityType,
                            profileId: profileId,
                        });
                    }}
                />
            ),
        },
    ];
    useSetHeaderTitle(intl('header'));
    const navigation = useProfilesTabNavigation();

    return (
        <AnimateLoadedContent isLoading={proCareTeamResourcesData.status === 'loading'}>
            {() => (
                <>
                    <ProfileHeaderContainer profileId={profileId}>
                        <TabView tabs={tabs} selectedTab={tab} onChange={(_) => setTab(_ as Tab)} />
                    </ProfileHeaderContainer>
                    {addProCareTeamResourcesSideDrawer.SideDrawer}
                </>
            )}
        </AnimateLoadedContent>
    );
};

export type CareTeamScreenOwnProps = {};

export type CareTeamScreenProps = CareTeamScreenOwnProps & CareTeamLandingNavigatorProps;

export type CareTeamLandingNavigatorProps = {
    route: RouteProp<ProfilesTabParamList, 'care-team'>;
};

type Tab = 'list' | 'notes';

const CareTeamScreen: React.FC<CareTeamScreenProps> = (props) => {
    const { profileId } = props.route.params;

    return (
        <ProCareTeamResourcesProvider profileId={profileId}>
            {(careTeamScreenContentsProps) => <CareTeamScreenContents {...careTeamScreenContentsProps} />}
        </ProCareTeamResourcesProvider>
    );
};

export default React.memo(CareTeamScreen);
